/**
 * Shared formatting helpers for the game UI.
 */
import { Suit, Jewel, type Card } from '@pokercubed/shared';

const SUIT_SYMBOLS: Record<Suit, string> = {
  [Suit.HEARTS]: '♥',
  [Suit.DIAMONDS]: '♦',
  [Suit.CLUBS]: '♣',
  [Suit.SPADES]: '♠',
};

/** Compact card display for a specific rank+suit: `{rank: 'A', suit: DIAMONDS}` -> 'A♦'. */
export const formatCardShort = (card: Card): string => `${card.rank}${SUIT_SYMBOLS[card.suit]}`;

const JEWEL_NAMES: Record<Jewel, string> = {
  [Jewel.RUBY]: 'Ruby',
  [Jewel.EMERALD]: 'Emerald',
  [Jewel.TOPAZ]: 'Topaz',
  [Jewel.ONYX]: 'Onyx',
};

/** Capitalized jewel name for display: 'ruby' -> 'Ruby'. */
export const formatJewelName = (jewel: Jewel): string => JEWEL_NAMES[jewel];

/** Compact chip-count display: 1500 -> '1.5K', 2400000 -> '2.4M'. */
export const formatChips = (chips: number): string => {
  if (chips >= 1000000) {
    return `${(chips / 1000000).toFixed(1)}M`;
  } else if (chips >= 1000) {
    return `${(chips / 1000).toFixed(1)}K`;
  }
  return chips.toString();
};

const PHASE_NAMES: Record<string, string> = {
  ANTE: 'Ante',
  PRE_FLOP: 'Pre-Flop',
  ROUND_1: 'Round 1 - Corners',
  ROUND_2: 'Round 2 - Diamond',
  ROUND_3: 'Round 3 - Center',
  SHOWDOWN: 'Showdown',
  ENDED: 'Hand Ended',
};

/** Human-readable game phase name. */
export const formatPhase = (phase: string): string => PHASE_NAMES[phase] || phase;
